// React
import React, { useState } from "react";
import { useTranslation } from "react-i18next";

// MUI
import useMediaQuery from "@mui/material/useMediaQuery";

// Third party imports
import { useNavigate } from "react-router-dom";

// Components
import Modal from "components/ui/modal";
import NotificationHeader from "./_components/NotificationHeader";
import NotificationsList from "./_components/NotificationsList";
import NotificationFooter from "./_components/NotificationFooter";
import NotificationSettings from "./_components/NotificationSettings";

// Utils
import {
  handleNotificationClick as handleNotificationClickUtil,
} from "utils/notificationHelpers";

// Constants
import { SIDEBAR_WIDTH, SIDEBAR_WIDTH_COLLAPSED } from "utils/consts";

// Hooks
import useGetNotifications from "hooks/notifications/useGetNotifications";

// Styles
import "./style.css";

const NotificationPopover = ({
  open,
  onClose,
  isSidebarCollapsed = false
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  // State
  const [activeTab, setActiveTab] = useState(0); // 0 = unread, 1 = read
  const [settingsModalOpen, setSettingsModalOpen] = useState(false);

  // Use unified notifications hook for popover
  const {
    loading,
    currentNotifications,
    markNotificationAsRead,
    markAllAsRead,
  } = useGetNotifications({
    activeTab,
    latestOnly: true, // Popover mode
    open,
  });

  // Position calculation
  const sidebarWidth = isSidebarCollapsed ?
    SIDEBAR_WIDTH_COLLAPSED :
    SIDEBAR_WIDTH;
  // On phones the sidebar is not beside the popover: keep it inside the viewport
  const isNarrow = useMediaQuery("(max-width:767px)");
  const popoverLeftPosition = isNarrow ? 16 : sidebarWidth + 10;

  const handleNotificationClick = async (notification) => {
    await handleNotificationClickUtil(notification, navigate, markNotificationAsRead, onClose);
  };

  const handleViewAll = () => {
    onClose();
    const hashPath = activeTab === 0 ? '#unread' : '#read';
    navigate(`/notifications${hashPath}`);
  };

  const handleTabChange = (tabIndex) => {
    setActiveTab(tabIndex);
  };

  const handleMarkAllAsRead = async () => {
    await markAllAsRead();
  };

  const handleSettingsClick = () => {
    setSettingsModalOpen(true);
  };

  const closeSettingsModal = () => {
    setSettingsModalOpen(false);
  };

  // Title and tabs live in the Modal header and "View all" in its footer:
  // only the list scrolls in the Modal body
  const header = (
    <div className="flex min-w-0 flex-col gap-3">
      <h2 className="text-lg font-semibold text-gray-900 break-words">
        {t('notificationPopover:notifications', 'Notifications')}
      </h2>
      <NotificationHeader
        activeTab={activeTab}
        onTabChange={handleTabChange}
        onMarkAllAsRead={handleMarkAllAsRead}
        onSettingsClick={handleSettingsClick}
      />
    </div>
  );

  return (
    <>
      <Modal
        open={open}
        header={header}
        ariaLabel={t('notificationPopover:notifications', 'Notifications')}
        footer={<NotificationFooter onViewAll={handleViewAll} />}
        onClose={onClose}
        maxWidth="500px"
        height="auto"
        maxHeight="calc(100dvh - 116px)"
        showCloseButton={true}
        className="notification-popover"
        reduceBackdrop={true}
        disableBackdrop={false}
        disableCentering={false}
        style={{
          left: `${popoverLeftPosition}px`,
          top: '100px',
          width: isNarrow ? 'calc(100vw - 32px)' : '40%',
        }}
      >
        <NotificationsList
          loading={loading}
          notifications={currentNotifications}
          onNotificationClick={handleNotificationClick}
        />
      </Modal>

      <NotificationSettings
        open={settingsModalOpen}
        onClose={closeSettingsModal}
      />
    </>
  );
};

export default NotificationPopover;
